// Family tree / dependents — for asylum derivatives, family-petition
// beneficiaries, estate beneficiaries, custody dependents. Reads
// matter.workspace.family (preferred) or matter.workspace.derivatives /
// matter.workspace.beneficiaries (fallbacks for legacy data).
//
// Each row's name is an EntityLink kind="relative" pointing at the
// owning client's relatives tab — keeping the click-depth promise
// (matter widget → client/relatives → relative detail) without needing
// a dedicated /relative/:id route.

import { EntityLink } from "../../chrome/EntityLink";
import { pick, type WidgetProps } from "./types";
import { WidgetCard } from "./WidgetCard";

interface FamilyMember {
  name?: string;
  relationship?: string;
  age?: number | string;
  status?: string;
}

export function FamilyTreeWidget({ config, matter }: WidgetProps) {
  const family =
    pick<FamilyMember[]>(matter.workspace, "family") ??
    pick<FamilyMember[]>(matter.workspace, "derivatives") ??
    pick<FamilyMember[]>(matter.workspace, "beneficiaries") ??
    [];
  const title = config.title ?? "Family";
  // The id rides in the link's hash as <eID>:<index>; the relatives tab
  // does not anchor its rows yet, so the link opens the tab.
  const compositeKey = (i: number) => `${matter.eID}:${i}`;

  if (family.length === 0) {
    return (
      <WidgetCard title={title}>
        <div style={{ fontSize: 12.5, color: "var(--muted)" }}>
          No family members on this matter.
        </div>
      </WidgetCard>
    );
  }

  return (
    <WidgetCard title={title} sub={`${family.length}`}>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {family.map((member, i) => (
          <div
            key={i}
            style={{
              display: "flex",
              alignItems: "baseline",
              justifyContent: "space-between",
              padding: "6px 8px",
              borderRadius: 6,
              border: "1px solid var(--line-2)",
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--ink)" }}>
                {member.name ? (
                  <EntityLink
                    kind="relative"
                    id={compositeKey(i)}
                    clientId={matter.clientId}
                    subtle
                  >
                    {member.name}
                  </EntityLink>
                ) : (
                  "(no name)"
                )}
              </div>
              <div style={{ fontSize: 11, color: "var(--muted)" }}>
                {member.relationship ?? "—"}
                {member.age != null ? ` · age ${member.age}` : ""}
              </div>
            </div>
            {member.status && (
              <span
                style={{
                  fontSize: 10.5,
                  padding: "2px 6px",
                  borderRadius: 4,
                  background: "var(--bg)",
                  border: "1px solid var(--line)",
                  color: "var(--ink-2)",
                  whiteSpace: "nowrap",
                  marginLeft: 8,
                }}
              >
                {member.status}
              </span>
            )}
          </div>
        ))}
      </div>
    </WidgetCard>
  );
}
